import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteSessionFromStorage,
  loadMessagesFromStorage,
  saveMessagesToStorage,
  useAIAssistantChat,
} from '@/hooks/use-ai-assistant-chat';
import { useGenerativeUIEvents } from '@/hooks/use-generative-ui-events';
import { initAgentConfig } from '@/lib/agent-config';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useMode } from '@/providers/ModeProvider';
import { setAgentConfigAuthProvider } from '@/services/agent-config.service';
import { setStreamAuthProvider } from '@/services/agent-stream.service';
import { aiAssistantService, setAssistantServiceAuthProvider } from '@/services/ai-assistant.service';
import type { AIAssistantContextType, AIMessage, AISession } from '@/types/ai-assistant.types';

const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined);

export { AIAssistantContext };

export function AIAssistantProvider({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
  const { getHeaders, apiBaseUrl } = useApiConfig();
  const { getAccessToken, instanceId } = useAuth();
  const { isDemo } = useMode();

  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sessions, setSessions] = useState<AISession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const sessionsLoadedRef = useRef(false);

  // Inject auth providers into module-scoped services
  useEffect(() => {
    setAgentConfigAuthProvider(getHeaders, apiBaseUrl);
    setAssistantServiceAuthProvider(apiBaseUrl, getHeaders);
    if (getAccessToken) {
      setStreamAuthProvider(getAccessToken, instanceId);
    }
  }, [getHeaders, apiBaseUrl, getAccessToken, instanceId]);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  const savedMessageCountRef = useRef<number>(0);
  const lastSavedSessionIdRef = useRef<string | null>(null);

  const handleBackgroundResponse = useCallback(
    async (sessionId: string, message: AIMessage) => {
      const existingMessages = loadMessagesFromStorage(sessionId);
      const updatedMessages = [...existingMessages, message];
      saveMessagesToStorage(updatedMessages, sessionId);

      if (backendAvailable !== false) {
        try {
          await aiAssistantService.addMessage(sessionId, {
            role: message.role,
            content: message.content,
            timestamp: message.timestamp,
            toolCall: message.toolCall
              ? {
                  id: message.toolCall.id,
                  name: message.toolCall.name,
                  status: message.toolCall.status,
                  result: message.toolCall.result,
                }
              : undefined,
          });
        } catch {
          // Non-critical failure
          // Silently ignore backend save failures
        }
      }
    },
    [backendAvailable],
  );

  const getCurrentSessionId = useCallback(() => {
    return currentSessionIdRef.current;
  }, []);

  const {
    messages,
    isProcessing,
    currentTool,
    sendMessage: sendChatMessage,
    clearHistory: clearChatHistory,
    setMessages,
    cancelProcessing,
    resetConversation,
    streamingText,
    thinkingMessage,
  } = useAIAssistantChat({
    isDemo,
    onMessageReceived: () => {
      if (!isOpen) {
        setUnreadCount((prev) => prev + 1);
      }
    },
    onBackgroundResponse: (sessionId: string, message: AIMessage) => {
      void handleBackgroundResponse(sessionId, message);
    },
    getCurrentSessionId,
  });

  // Keyboard shortcut (Cmd/Ctrl + .)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-close on navigation
  useEffect(() => {
    const handleNavigation = (): void => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    const handleClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const navLink = target.closest('a[href], button[data-nav]');
      if (navLink && isOpen) {
        setTimeout(() => setIsOpen(false), 100);
      }
    };

    window.addEventListener('popstate', handleNavigation);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      document.removeEventListener('click', handleClick);
    };
  }, [isOpen]);

  const openPanel = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const getPageContext = useCallback((): string => {
    const path = window.location.pathname;

    if (path.includes('/customers')) return 'customers';
    if (path.includes('/transactions')) return 'transactions';
    if (path.includes('/collections')) return 'collections';
    if (path.includes('/disputes')) return 'disputes';
    if (path.includes('/payments')) return 'payments';
    if (path.includes('/credit-scoring')) return 'credit-scoring';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/tasks')) return 'tasks';
    if (path === '/') return 'dashboard';

    return 'general';
  }, []);

  const refreshSessions = useCallback(async () => {
    if (backendAvailable === false) return;
    try {
      const fetchedSessions = await aiAssistantService.getSessions();
      setSessions(
        fetchedSessions.map((s) => ({
          id: s.id,
          title: s.title,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          messageCount: s.messageCount,
        })),
      );
      setBackendAvailable(true);
    } catch {
      // Non-critical failure
      setBackendAvailable(false);
    }
  }, [backendAvailable]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!currentSessionIdRef.current && backendAvailable !== false) {
        try {
          const newSession = await aiAssistantService.createSession();
          currentSessionIdRef.current = newSession.id;
          lastSavedSessionIdRef.current = newSession.id;
          savedMessageCountRef.current = 0;
          setCurrentSessionId(newSession.id);
          setSessions((prev) => [
            {
              id: newSession.id,
              title: newSession.title,
              createdAt: newSession.createdAt,
              updatedAt: newSession.updatedAt,
              messageCount: newSession.messageCount,
            },
            ...prev,
          ]);
        } catch {
          // Non-critical failure
        }
      }

      const context = getPageContext();
      await sendChatMessage(content, context);
    },
    [sendChatMessage, getPageContext, backendAvailable],
  );

  const handleUserAction = useCallback(
    (content: string): void => {
      void sendMessage(content);
    },
    [sendMessage],
  );

  useGenerativeUIEvents({
    onUserAction: handleUserAction,
    enabled: !isProcessing,
  });

  const clearHistory = useCallback(() => {
    clearChatHistory();
  }, [clearChatHistory]);

  const markAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const loadSession = useCallback(
    async (sessionId: string) => {
      if (currentSessionIdRef.current && messages.length > 0) {
        saveMessagesToStorage(messages, currentSessionIdRef.current);
      }

      resetConversation();

      if (backendAvailable === false) {
        const localMessages = loadMessagesFromStorage(sessionId);
        setMessages(localMessages);
        setCurrentSessionId(sessionId);
        return;
      }

      try {
        const sessionDetail = await aiAssistantService.getSession(sessionId);
        const backendMessages: AIMessage[] = sessionDetail.messages.map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant' | 'system',
          content: m.content,
          timestamp: m.timestamp,
          toolCall: m.toolCall
            ? {
                id: m.toolCall.id,
                name: m.toolCall.name,
                status: m.toolCall.status as 'pending' | 'running' | 'success' | 'error',
                result: m.toolCall.result,
              }
            : undefined,
        }));

        const localMessages = loadMessagesFromStorage(sessionId);
        const loadedMessages = localMessages.length > backendMessages.length ? localMessages : backendMessages;

        setMessages(loadedMessages);
        setCurrentSessionId(sessionId);
      } catch {
        // Non-critical failure
        const localMessages = loadMessagesFromStorage(sessionId);
        setMessages(localMessages);
        setCurrentSessionId(sessionId);
      }
    },
    [backendAvailable, setMessages, messages, resetConversation],
  );

  const startNewSession = useCallback(async () => {
    cancelProcessing();

    if (currentSessionIdRef.current && messages.length > 0) {
      saveMessagesToStorage(messages, currentSessionIdRef.current);
    }

    resetConversation();

    if (backendAvailable === false) {
      clearChatHistory();
      setCurrentSessionId(null);
      return;
    }
    try {
      const newSession = await aiAssistantService.createSession();
      setCurrentSessionId(newSession.id);
      lastSavedSessionIdRef.current = newSession.id;
      savedMessageCountRef.current = 0;
      clearChatHistory();
      setSessions((prev) => [
        {
          id: newSession.id,
          title: newSession.title,
          createdAt: newSession.createdAt,
          updatedAt: newSession.updatedAt,
          messageCount: newSession.messageCount,
        },
        ...prev,
      ]);
    } catch {
      // Non-critical failure
      clearChatHistory();
      setCurrentSessionId(null);
    }
  }, [backendAvailable, clearChatHistory, cancelProcessing, messages, resetConversation]);

  const deleteSessionHandler = useCallback(
    async (sessionId: string) => {
      deleteSessionFromStorage(sessionId);

      if (currentSessionIdRef.current === sessionId) {
        clearChatHistory();
        setCurrentSessionId(null);
      }

      setSessions((prev) => prev.filter((s) => s.id !== sessionId));

      if (backendAvailable === false) return;

      try {
        await aiAssistantService.deleteSession(sessionId);
      } catch {
        // Non-critical failure
      }
    },
    [backendAvailable, clearChatHistory],
  );

  // Lazy-load sessions and agent config when panel is first opened
  useEffect(() => {
    if (!isOpen || sessionsLoadedRef.current) return;
    sessionsLoadedRef.current = true;

    const loadSessions = async (): Promise<void> => {
      try {
        const fetchedSessions = await aiAssistantService.getSessions();
        setSessions(
          fetchedSessions.map((s) => ({
            id: s.id,
            title: s.title,
            createdAt: s.createdAt,
            updatedAt: s.updatedAt,
            messageCount: s.messageCount,
          })),
        );
        setBackendAvailable(true);
      } catch {
        // Non-critical failure
        setBackendAvailable(false);
      }
    };

    void loadSessions();
    void initAgentConfig();
  }, [isOpen]);

  // Save new messages to backend when they're added
  useEffect(() => {
    const saveNewMessagesToBackend = async (): Promise<void> => {
      const sessionId = currentSessionIdRef.current;
      if (!sessionId || backendAvailable === false) return;

      if (lastSavedSessionIdRef.current !== sessionId) {
        lastSavedSessionIdRef.current = sessionId;
        savedMessageCountRef.current = messages.length;
        return;
      }

      if (messages.length <= savedMessageCountRef.current) return;

      const newMessages = messages.slice(savedMessageCountRef.current);

      try {
        const messageDtos = newMessages.map((message) => ({
          role: message.role,
          content: message.content,
          timestamp: message.timestamp,
          toolCall: message.toolCall
            ? {
                id: message.toolCall.id,
                name: message.toolCall.name,
                status: message.toolCall.status,
                result: message.toolCall.result,
              }
            : undefined,
        }));
        await aiAssistantService.addMessages(sessionId, messageDtos);
      } catch {
        // Non-critical failure
      }

      savedMessageCountRef.current = messages.length;
    };

    void saveNewMessagesToBackend();
  }, [messages, backendAvailable]);

  const value = useMemo<AIAssistantContextType>(
    () => ({
      isOpen,
      messages,
      isProcessing,
      unreadCount,
      currentTool,
      streamingText,
      thinkingMessage,
      openPanel,
      closePanel,
      togglePanel,
      sendMessage,
      clearHistory,
      markAsRead,
      sessions,
      currentSessionId,
      loadSession,
      startNewSession,
      deleteSession: deleteSessionHandler,
      refreshSessions,
      cancelProcessing,
    }),
    [
      isOpen,
      messages,
      isProcessing,
      unreadCount,
      currentTool,
      streamingText,
      thinkingMessage,
      openPanel,
      closePanel,
      togglePanel,
      sendMessage,
      clearHistory,
      markAsRead,
      sessions,
      currentSessionId,
      loadSession,
      startNewSession,
      deleteSessionHandler,
      refreshSessions,
      cancelProcessing,
    ],
  );

  return <AIAssistantContext.Provider value={value}>{children}</AIAssistantContext.Provider>;
}

export function useAIAssistant(): AIAssistantContextType {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within AIAssistantProvider');
  }
  return context;
}
